"use client";

import { Button } from "@/components/ui/button";
import { Palette, Check } from "lucide-react";
import { useState } from "react";
import { useTheme } from "./theme-provider";

type ThemeOption = {
  name: string;
  value: string;
  colors: string[];
};

const themes: ThemeOption[] = [
  { name: "Default", value: "default", colors: ["#000000", "#000000"] },
  {
    name: "Purple & Teal",
    value: "purple-teal",
    colors: ["#8A4FFF", "#40C0D0"],
  },
  {
    name: "Blue & Orange",
    value: "blue-orange",
    colors: ["#4169E1", "#FF8C00"],
  },
  { name: "Green & Pink", value: "green-pink", colors: ["#4CAF50", "#FF69B4"] },
  { name: "Vibrant", value: "vibrant", colors: ["#FF3333", "#8833FF"] },
  { name: "Sunset", value: "sunset", colors: ["#FF8800", "#FF3377"] },
  { name: "Ocean", value: "ocean", colors: ["#0066CC", "#33CCCC"] },
];

export default function ThemeSelector() {
  const [isOpen, setIsOpen] = useState(false);
  const { theme, setTheme } = useTheme();

  // Function to handle theme change
  const handleThemeChange = (value: string) => {
    setTheme(
      value as
        | "default"
        | "purple-teal"
        | "blue-orange"
        | "green-pink"
        | "vibrant"
        | "sunset"
        | "ocean"
    );
    setIsOpen(false);
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <div className="relative">
        <Button
          variant="outline"
          size="icon"
          className="rounded-full glassmorphism-button shadow-md hover:shadow-lg transition-all duration-300"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Change theme"
        >
          <Palette className="h-5 w-5" />
        </Button>

        {isOpen && (
          <div className="absolute bottom-full right-0 mb-2 w-64 rounded-xl glassmorphism p-3 shadow-lg border border-border/20 animate-in fade-in-50 slide-in-from-bottom-5 duration-300">
            <div className="space-y-2">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium">Select Theme</p>
                <button
                  className="text-muted-foreground hover:text-foreground text-xs"
                  onClick={() => setIsOpen(false)}
                >
                  Close
                </button>
              </div>
              <div className="grid grid-cols-1 gap-2">
                {themes.map((t) => (
                  <button
                    key={t.name}
                    className={`w-full rounded-lg px-3 py-2 text-sm text-left transition-all duration-200 hover:bg-primary/10 flex items-center justify-between ${
                      theme === t.value ? "bg-primary/20 shadow-sm" : ""
                    }`}
                    onClick={() => handleThemeChange(t.value)}
                  >
                    <div className="flex items-center gap-2">
                      <div className="flex space-x-1">
                        {t.colors.map((color, i) => (
                          <div
                            key={i}
                            className="w-4 h-4 rounded-full border border-border/30"
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                      <span>{t.name}</span>
                    </div>
                    {theme === t.value && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
